import { permissionsByEntity, useCheckPermissions } from '@cmg/auth';
import { UUID } from '@cmg/common';
import { Button, SnackbarManager } from '@cmg/design-system';
import React, { useCallback, useMemo } from 'react';

import {
  AllocationAcknowledgement,
  AllocationAcknowledgementNewStatus,
} from '../../../../../../../graphql';
import { updateDemandGridRowsReactiveVar } from '../../../../../../order-book/institutional-demand/hooks/useUpdateDemandGridRowsReactiveVar';
import { useManageIndication_MdlAllocationRankingLazyQuery } from '../../manage-indication/graphql/__generated__';
import { useIndicationActivity_AcknowledgeMyFinalAllocationMutation } from '../graphql/__generated__';

export type Props = Readonly<{
  offeringId?: UUID;
  allocationVersion?: string;
  getAllocationRanking: ReturnType<typeof useManageIndication_MdlAllocationRankingLazyQuery>[0];
  investorReplyStatus: AllocationAcknowledgement;
}>;

const useBuySideAllocation = ({
  offeringId,
  allocationVersion,
  investorReplyStatus,
  getAllocationRanking,
}: Props) => {
  const hasIoiRead = useCheckPermissions([permissionsByEntity.Ioi.READ]);

  const [acknowledgeMyAllocation, { loading: updateMyStatusLoading, error: updateMyStatusError }] =
    useIndicationActivity_AcknowledgeMyFinalAllocationMutation();

  const handleAcknowledge = useCallback(
    async (status: AllocationAcknowledgementNewStatus) => {
      try {
        if (!offeringId || !allocationVersion) {
          throw new Error('No offeringId of allocationVersion was provided');
        }

        if (status === AllocationAcknowledgementNewStatus.Acknowledged) {
          await acknowledgeMyAllocation({
            variables: {
              offeringId: offeringId,
              versionId: allocationVersion,
            },
          });

          SnackbarManager.success('Successfully acknowledged allocation');
          if (hasIoiRead) {
            getAllocationRanking?.({ variables: { offeringId: offeringId! } });
          }

          updateDemandGridRowsReactiveVar({ fullRefreshRequired: true });
        }
      } catch {
        SnackbarManager.error('Failed to acknowledge allocation');
      }
    },
    [acknowledgeMyAllocation, allocationVersion, getAllocationRanking, hasIoiRead, offeringId]
  );

  const errors = useMemo(
    () =>
      [updateMyStatusError]
        .map(error => error?.message)
        .filter((val): val is string => Boolean(val)),
    [updateMyStatusError]
  );

  const isAcknowledgeVisible = investorReplyStatus === AllocationAcknowledgement.Unacknowledged;

  const actionButtons = useMemo(
    () => [
      ...(isAcknowledgeVisible
        ? [
            <Button
              key="acknowledge"
              variant="contained"
              onClick={() => handleAcknowledge(AllocationAcknowledgementNewStatus.Acknowledged)}
              disabled={updateMyStatusLoading}
            >
              Acknowledge
            </Button>,
          ]
        : []),
    ],
    [handleAcknowledge, isAcknowledgeVisible, updateMyStatusLoading]
  );

  return {
    actionButtons,
    errors,
  };
};

export default useBuySideAllocation;
